"use client";

import TranscriptionView from "@/components/TranscriptionView";
import VoiceAgent from "@/components/VoiceAgent";
import { BrandMark } from "@/components/brand";
import { TrackedRoomAudioRenderer } from "@/components/livekit/TrackedRoomAudioRenderer";
import languages from "@/data/languages.json";
import useLiveKitAgent from "@/hooks/useLiveKitAgent";
import { useUserPreferences } from "@/hooks/useLocalStorage";
import { shouldAutoStartVoiceDemo } from "@/lib/voice-session-recovery";
import { RoomContext } from "@livekit/components-react";
import { X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { getTutorDemoLevel } from "./experience";

const MAX_DEMO_SECONDS = 300;

export type TutorDemoCompletionReason = "tutor_finished" | "time_limit";

export function TutorSpeakingDemo({
  languageCode,
  languageName,
  cefrLevel,
  onStarted,
  onFinished,
}: {
  languageCode: string;
  languageName: string;
  cefrLevel: string;
  onStarted: () => void;
  onFinished: (reason: TutorDemoCompletionReason, durationSeconds: number) => void;
}) {
  const { preferences, examplesBeforePracticeOverride } = useUserPreferences();
  const [, setInteractionMode] = useState<"passive" | "text" | "voice">("passive");
  const autoStartedLanguageRef = useRef<string | null>(null);
  const trackedStartRef = useRef(false);
  const trackedFinishRef = useRef(false);
  const languageLevel = getTutorDemoLevel(cefrLevel);
  const nativeLanguageCode = preferences.nativeLanguageCode;
  const nativeLanguageName = nativeLanguageCode
    ? Object.entries(languages as Record<string, string>).find(
        ([, code]) => code === nativeLanguageCode
      )?.[0]
    : undefined;

  const {
    room,
    isConnected,
    isRecording,
    recordingTime,
    toggleRecording,
    disconnectFromLiveKit,
    connectionState,
    connectionError,
    retryConnection,
    audioTrackError,
    audioRetryCount,
    retryAudioTrack,
    clearAudioErrors,
  } = useLiveKitAgent({
    selectedLanguageCode: languageCode,
    selectedLanguageLevel: languageLevel,
    selectedVoice: (preferences.voice || "Aoede") as any,
    examplesBeforePractice: examplesBeforePracticeOverride,
    selectedTeachingStyle: (preferences.teachingStyle || "neutral") as any,
    allowUnauthenticatedSessions: true,
    guestSessionLimitSeconds: MAX_DEMO_SECONDS,
    fromLanguageName: nativeLanguageName,
    fromLanguageCode: nativeLanguageCode,
    sessionMode: "conversation",
    analyticsSurface: "onboarding",
    setInteractionMode,
    subscribed: false,
  });

  useEffect(() => {
    if (
      shouldAutoStartVoiceDemo({
        selectedLanguageCode: languageCode,
        connectionState,
        isRecording,
        isConnected,
        hasAutoStarted: autoStartedLanguageRef.current === languageCode,
      })
    ) {
      autoStartedLanguageRef.current = languageCode;
      toggleRecording("conversation");
    }
  }, [connectionState, isConnected, isRecording, languageCode, toggleRecording]);

  useEffect(() => {
    if (!isConnected || !isRecording || trackedStartRef.current) return;
    trackedStartRef.current = true;
    onStarted();
  }, [isConnected, isRecording, onStarted]);

  const finishDemo = (reason: TutorDemoCompletionReason) => {
    if (trackedFinishRef.current) return;
    trackedFinishRef.current = true;
    disconnectFromLiveKit(reason === "time_limit", false, "tutor_onboarding_demo_end");
    onFinished(reason, recordingTime);
  };

  useEffect(() => {
    if (isRecording && recordingTime >= MAX_DEMO_SECONDS) finishDemo("time_limit");
  }, [isRecording, recordingTime]);

  const remaining = Math.max(0, MAX_DEMO_SECONDS - (recordingTime || 0));
  const minutes = String(Math.floor(remaining / 60)).padStart(2, "0");
  const seconds = String(remaining % 60).padStart(2, "0");

  return (
    <div className="fixed inset-0 z-50 bg-[#0F0F14]">
      <div className="relative z-10 flex h-[100dvh] flex-col">
        <div className="flex min-h-16 items-center justify-between gap-3 border-b border-white/10 bg-[#0F0F14] px-3 py-3 sm:px-6">
          <div className="flex min-w-0 items-center gap-3">
            <BrandMark size={36} className="h-9 w-9 shrink-0 object-contain" alt="" priority />
            <div className="min-w-0 text-sm">
              <div className="truncate font-bold text-white">
                Student view · {languageName} {cefrLevel}
              </div>
              <div className="text-white/40">Experience the practice yourself</div>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-2 sm:gap-3">
            <div
              className="rounded-lg border border-white/10 bg-[#15161B] px-3 py-2 font-mono text-sm text-[#FD954F] sm:text-base"
              aria-label={`${minutes} minutes and ${seconds} seconds remaining`}
            >
              {minutes}:{seconds}
            </div>
            <button
              type="button"
              onClick={() => finishDemo("tutor_finished")}
              className="flex min-h-11 items-center gap-2 rounded-lg border border-white/15 bg-[#15161B] px-3 py-2 text-sm font-semibold text-white/70 transition hover:border-[#FD954F]/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F] sm:px-4"
              aria-label="Finish demo"
            >
              <span className="hidden sm:inline">Finish demo</span>
              <X className="h-5 w-5 sm:hidden" aria-hidden="true" />
            </button>
          </div>
        </div>

        <div className="relative flex-1">
          <RoomContext.Provider value={room}>
            {isRecording && isConnected && (
              <TrackedRoomAudioRenderer
                activeSession={isRecording && isConnected}
                roomName={room.name || null}
                surface="tutor-onboarding-speaking-demo"
              />
            )}
            {connectionState === "connecting" && (
              <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-lg border border-[#FD954F]/30 bg-[#15161B] px-4 py-2 text-center text-sm font-semibold text-white">
                Chicky is getting the conversation ready…
              </div>
            )}
            {isRecording && recordingTime < 5 && (
              <div className="absolute left-1/2 top-4 z-20 -translate-x-1/2 rounded-lg border border-[#FD954F]/30 bg-[#15161B] px-4 py-2 text-center text-sm font-semibold text-white motion-safe:animate-fade-in">
                Start with hello. Notice how Chicky keeps the learner talking.
              </div>
            )}

            <div className="flex items-center justify-center border-b border-white/10 bg-[#121318] py-4">
              <VoiceAgent
                room={room}
                isRecording={Boolean(isRecording)}
                renderAudioRenderer={false}
              />
            </div>
            <TranscriptionView
              onClose={() => finishDemo("tutor_finished")}
              connectionState={connectionState}
              connectionError={connectionError}
              retryConnection={retryConnection}
              audioTrackError={audioTrackError}
              audioRetryCount={audioRetryCount}
              retryAudioTrack={retryAudioTrack}
              clearAudioErrors={clearAudioErrors}
              sessionDuration={recordingTime}
              selectedLanguageCode={languageCode}
              selectedLanguageLevel={languageLevel}
              isActiveSession={Boolean(isRecording && isConnected)}
              showInactiveSessionRecovery
              recoverySurface="onboarding_quiz"
              renderRoomAudio={false}
            />
          </RoomContext.Provider>
        </div>
      </div>
    </div>
  );
}
